import React from 'react';
import { CalendarIcon, CheckIcon, ClockIcon, MapPinIcon, TriangleAlertIcon } from 'lucide-react';
import { consciousnessLevels, webinarAudience, webinarDetails, webinarLearnings } from '../data/webinar';
interface WebinarProps {
  whatsappGroupUrl: string;
}
const HOST_IMAGE = 'https://cdn.magicpatterns.com/uploads/6TvPxZWzcZuaRKqJtUmZh1/ChatGPT_Image_12_sept_2026,_04_21_30_p.m..png';
const detailIcons = [CalendarIcon, ClockIcon, MapPinIcon];
function JoinButton({
  href,
  label
}: {
  href: string;
  label: string;
}) {
  return <a href={href} target="_blank" rel="noopener noreferrer" className="inline-flex w-full items-center justify-center rounded-sm bg-dorado px-8 py-5 text-center font-sans text-lg font-bold text-noche transition-colors duration-150 ease-out hover:bg-dorado-deep focus:outline-none focus-visible:ring-2 focus-visible:ring-hueso focus-visible:ring-offset-2 focus-visible:ring-offset-violeta sm:w-auto sm:text-xl" data-id="element-167" data-mp="xpehqficsve9">
      {label}
    </a>;
}
export function Webinar({
  whatsappGroupUrl
}: WebinarProps) {
  return <div className="flex min-h-full w-full flex-col bg-violeta text-hueso" data-id="element-168" data-mp="zvpd0qtoja8s">
      <main className="mx-auto w-full max-w-4xl flex-1 px-6 py-16 sm:py-20" data-id="element-169" data-mp="zxsce1gq2fq6">
        <header className="grid items-center gap-10 md:grid-cols-[1.15fr_0.85fr]" data-id="element-170" data-mp="m6drtq7626h0">
          <div data-id="element-171" data-mp="r4fbac8i87t0">
            <p className="font-sans text-sm font-semibold tracking-[0.18em] text-dorado" data-id="element-172" data-mp="r3vpuljnb1a8">
              WEBINAR EN VIVO Y GRATUITO
            </p>
            <h1 className="mt-6 font-serif text-4xl leading-[1.15] sm:text-5xl" data-id="element-173" data-mp="1ivbojdyh8egt">
              Cómo Salir del Nivel de Dolor y Volver a Amar sin Miedo
            </h1>
            <p className="mt-6 font-sans text-lg leading-relaxed text-hueso/80" data-id="element-174" data-mp="14xeog7ktdpxe">
              Vas a aprender las herramientas de PNL exactas que uso con mis clientes para soltar el
              apego, subir de nivel de consciencia, y prepararte para tu próxima relación desde la
              plenitud — no desde el miedo.
            </p>
          </div>

          <figure className="md:justify-self-end" data-id="element-175" data-mp="3ah6qu7wl6ubi">
            <img src={HOST_IMAGE} alt="Freddy Pimentel, coach de relaciones, frente al micrófono durante una transmisión en vivo" className="w-full max-w-sm rounded-sm object-cover md:max-w-none" width={1405} height={1130} data-id="element-176" data-mp="36gmqywkmpclo" />
            <figcaption className="mt-3 font-sans text-sm text-hueso/55" data-id="element-177" data-mp="2rri8rfdu38dk">
              Freddy Pimentel · en vivo, contigo, durante 60 minutos
            </figcaption>
          </figure>
        </header>

        <section aria-label="Fecha y hora" className="mt-14 bg-hueso px-6 py-7 text-noche sm:px-8" data-id="element-178" data-mp="2iqseig481q1y">
          <ul className="grid gap-4 sm:grid-cols-3" data-id="element-179" data-mp="2ds8tchxgcian">
            {webinarDetails.map((detail, index) => {
            const Icon = detailIcons[index] ?? CalendarIcon;
            return <li key={detail} className="flex items-start gap-3" data-id="element-180" data-mp="c0vplw780gg6" data-mpi={index}>
                  <Icon className="mt-0.5 h-5 w-5 flex-none text-violeta" aria-hidden="true" data-id="element-181" data-mp="byj8fl9aea46" />
                  <span className="font-sans text-base font-medium leading-relaxed" data-id="element-182" data-mp="ji0xfe5avs84">{detail}</span>
                </li>;
          })}
          </ul>
        </section>

        <section aria-labelledby="aprender" className="mt-16" data-id="element-183" data-mp="tagfebglkgt0">
          <h2 id="aprender" className="font-serif text-3xl text-dorado" data-id="element-184" data-mp="y9j7kvbhasjk">
            Qué vas a aprender
          </h2>
          <ul className="mt-6 space-y-4" data-id="element-185" data-mp="maspj5zghqj">
            {webinarLearnings.map(item => <li key={item} className="flex items-start gap-3" data-id="element-186" data-mp="5jki4ytdgted">
                <CheckIcon className="mt-1 h-5 w-5 flex-none text-dorado" aria-hidden="true" data-id="element-187" data-mp="ajglqwy5qite" />
                <span className="font-sans text-base leading-relaxed text-hueso/85" data-id="element-188" data-mp="el24v01abtc1">{item}</span>
              </li>)}
          </ul>
        </section>

        <section aria-labelledby="niveles" className="mt-16" data-id="element-189" data-mp="1ly2h1e69lbd8">
          <h2 id="niveles" className="font-serif text-3xl" data-id="element-190" data-mp="1pzo05h9e6lvv">
            Los 5 niveles de consciencia después de una ruptura
          </h2>
          <p className="mt-4 max-w-lectura font-sans text-base leading-relaxed text-hueso/70" data-id="element-191" data-mp="1ojjyide4lny3">
            La mayoría se queda girando en los tres primeros durante años. En el webinar trabajamos
            el salto a los dos últimos.
          </p>

          <ol className="mt-8 space-y-px" data-id="element-192" data-mp="1tieg5c0cx6gm">
            {consciousnessLevels.map((level, index) => <li key={level.name} className={`flex items-baseline gap-5 px-5 py-4 ${level.destination ? 'bg-dorado text-noche' : 'bg-noche/30 text-hueso'}`} data-id="element-193" data-mp="27he5klvp1pdu" data-mpi={index}>
                <span className={`font-serif text-2xl ${level.destination ? 'text-noche/50' : 'text-hueso/35'}`} aria-hidden="true" data-id="element-194" data-mp="2h764fsatt4o4">
                  {index + 1}
                </span>
                <span className="flex-1" data-id="element-195" data-mp="2m7tr61s0uzcj">
                  <span className={`block font-sans text-base font-semibold ${level.destination ? 'text-noche' : 'text-hueso'}`} data-id="element-196" data-mp="2m4pjuvj9fnhn">
                    {level.name}
                  </span>
                  <span className={`mt-1 block font-sans text-sm leading-relaxed ${level.destination ? 'text-noche/75' : 'text-hueso/60'}`} data-id="element-197" data-mp="2vtwf3kafw9md">
                    {level.note}
                  </span>
                </span>
              </li>)}
          </ol>
        </section>

        <section aria-labelledby="para-quien" className="mt-16 border-l-2 border-dorado pl-6" data-id="element-198" data-mp="2d4ps3l2m4vur">
          <h2 id="para-quien" className="font-serif text-3xl" data-id="element-199" data-mp="2h509ruzrpapg">
            Este webinar es para ti si...
          </h2>
          <ul className="mt-6 space-y-3" data-id="element-200" data-mp="236s859rsdl7y">
            {webinarAudience.map(item => <li key={item} className="font-sans text-base leading-relaxed text-hueso/85" data-id="element-201" data-mp="2853idllztxqp">
                {item}
              </li>)}
          </ul>
        </section>

        <section aria-label="Confirmar mi lugar" className="mt-16" data-id="element-202" data-mp="8xd82bx2208s">
          <JoinButton href={whatsappGroupUrl} label="Confirmar mi lugar ahora" data-id="element-203" data-mp="8w24od7iuui7" />
          <p className="mt-4 font-sans text-sm leading-relaxed text-hueso/60" data-id="element-204" data-mp="4wjkxkrfspgy">
            Al confirmar, te unes directo al grupo donde vas a recibir el link y todos los
            recordatorios.
          </p>
        </section>

        <aside className="mt-12 flex items-start gap-3 border border-dorado/40 px-5 py-4" data-id="element-205" data-mp="slw2b7wx43uc">
          <TriangleAlertIcon className="mt-0.5 h-5 w-5 flex-none text-dorado" aria-hidden="true" data-id="element-206" data-mp="sk1hrupk6dqs" />
          <p className="font-sans text-base leading-relaxed text-hueso/85" data-id="element-207" data-mp="nom71bmukxyl">
            Cada sesión tiene cupos limitados — quiero poder acompañar a cada grupo de cerca, por eso
            no abro fechas ilimitadas.
          </p>
        </aside>

        <section aria-label="Tu primer paso" className="mt-16" data-id="element-208" data-mp="33cvwjkecbr33">
          <p className="max-w-lectura font-serif text-2xl leading-snug sm:text-3xl" data-id="element-209" data-mp="2ta0agefcpow3">
            No llegaste a esta página por casualidad. Si sientes que es momento de dejar de repetir
            la misma historia y subir de nivel de verdad, este es tu primer paso.
          </p>
          <div className="mt-8" data-id="element-210" data-mp="336br1glz50wc">
            <JoinButton href={whatsappGroupUrl} label="Sí, quiero mi lugar" data-id="element-211" data-mp="37zeq249it2jx" />
          </div>
        </section>
      </main>

      <footer className="border-t border-hueso/10" data-id="element-212" data-mp="3bzosj6y3tau1">
        <div className="mx-auto w-full max-w-4xl px-6 py-8" data-id="element-213" data-mp="3gzkvr3yz8y8j">
          <p className="font-serif text-lg text-dorado" data-id="element-214" data-mp="3gy02risb9ncs">Sube de Nivel</p>
          <p className="mt-1 font-sans text-sm text-hueso/50" data-id="element-215" data-mp="3lzpg5530ad41">
            Este webinar es 100% gratuito y en vivo.
          </p>
        </div>
      </footer>
    </div>;
}